import {
  addSearchHistory as addToStorage,
  clearSearchHistory as clearStorage,
  getSearchHistory,
  removeSearchHistoryItem,
} from '@src/utils/searchHistoryUtil';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useSearchHistory() {
  const [history, setHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const showTimeoutRef = useRef<NodeJS.Timeout>();
  const hideTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    setHistory(getSearchHistory());
  }, []);

  const addToHistory = useCallback((searchTerm: string) => {
    addToStorage(searchTerm);
    setHistory(getSearchHistory());
  }, []);

  const removeFromHistory = useCallback((searchTerm: string) => {
    removeSearchHistoryItem(searchTerm);
    setHistory(getSearchHistory());
  }, []);

  const clearHistory = useCallback(() => {
    clearStorage();
    setHistory([]);
  }, []);

  const showHistoryWithDelay = useCallback((inputValue: string) => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    if (inputValue.trim() === '' && getSearchHistory().length > 0) {
      showTimeoutRef.current = setTimeout(() => {
        setShowHistory(true);
      }, 150);
    }
  }, []);

  const hideHistoryWithDelay = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setShowHistory(false);
    }, 200);
  }, []);

  const hideHistoryImmediately = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    setShowHistory(false);
  }, []);

  const handleInputFocus = useCallback(
    (inputValue: string) => {
      showHistoryWithDelay(inputValue);
    },
    [showHistoryWithDelay],
  );

  const getFilteredHistory = useCallback((inputValue: string) => {
    const allHistory = getSearchHistory();
    if (inputValue.trim() === '') {
      return allHistory;
    }

    const lowercaseInput = inputValue.toLowerCase();
    return allHistory.filter((item) => item.toLowerCase().includes(lowercaseInput));
  }, []);

  const handleInputChange = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    const allHistory = getSearchHistory();
    if (allHistory.length > 0) {
      setShowHistory(true);
    } else {
      setShowHistory(false);
    }
  }, []);

  const handleInputBlur = useCallback(() => {
    hideHistoryWithDelay();
  }, [hideHistoryWithDelay]);

  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return {
    history,
    showHistory,
    addToHistory,
    removeFromHistory,
    clearHistory,
    handleInputFocus,
    handleInputChange,
    handleInputBlur,
    hideHistoryImmediately,
    getFilteredHistory,
  };
}
